import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Select from 'react-select';
import { api, legacyPhpUrl } from './api/client';
import { useAuth } from './AuthContext';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import DescriptionIcon from '@mui/icons-material/Description';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import ConstructionIcon from '@mui/icons-material/Construction';
import ShieldIcon from '@mui/icons-material/Shield';
import BuildIcon from '@mui/icons-material/Build';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Kalan henüz porta edilmemiş PHP uçları (upload vs.) için relative base

const pad = (value) => String(value).padStart(2, '0');

const toDateTimeLocal = (date) => {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '';

  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const dateTimeLocalToBackend = (value) => {
  if (!value) return '';
  let s = String(value).replace('T', ' ').trim();
  // datetime-local → "YYYY-MM-DD HH:MM"; BE saniye de kabul eder, yine de pad et
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/.test(s)) s = `${s}:00`;
  return s;
};

const getDefaultStart = () => {
  const date = new Date();
  date.setHours(8, 0, 0, 0);
  return toDateTimeLocal(date);
};

const getDefaultEnd = () => {
  const date = new Date();
  date.setHours(18, 0, 0, 0);
  return toDateTimeLocal(date);
};

const generateIsEmriNo = () => {
  const date = new Date();
  return `${String(date.getFullYear()).slice(2)}${pad(date.getMonth() + 1)}${pad(date.getDate())}${pad(date.getHours())}${pad(date.getMinutes())}${pad(date.getSeconds())}_00`;
};

const parseJsonArray = (value) => {
  if (!value || value === 'null') return [];
  if (Array.isArray(value)) return value;

  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return String(value)
      .split(/\s*,\s*/)
      .map((item) => item.trim())
      .filter(Boolean);
  }
};

const splitDateTime = (date, time) => {
  if (!date) return '';
  const datePart = String(date).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(datePart)) return '';
  const match = String(time || '08:00').match(/(\d{1,2}):(\d{2})/);
  const hh = match ? match[1].padStart(2, '0') : '08';
  const mm = match ? match[2] : '00';
  return `${datePart}T${hh}:${mm}`;
};

const getPersonelName = (personel) => {
  if (!personel) return '';
  if (typeof personel === 'string') return personel;
  return [personel.isim, personel.soyisim].filter(Boolean).join(' ');
};

const getPersonelValue = (personel) => {
  const name = getPersonelName(personel);
  return personel?.id ? `${name}-${personel.id}` : name;
};

const splitCsv = (value) => String(value || '')
  .split(',')
  .map((part) => part.trim())
  .filter((part) => part.length);

const mapTeklifDoviz = (value) => {
  const code = String(value || '').trim().toUpperCase();
  if (code === 'EUR' || code === 'EURO') return 'EURO';
  if (code === 'USD') return 'USD';
  if (code === 'GBP') return 'GBP';
  if (code === 'TL' || code === 'TRY') return 'TL';
  return 'EURO';
};

const teklifMakineOptions = (baslik) => {
  const raw = String(baslik?.makine || '').trim();
  if (raw.includes('*')) {
    return raw.split(',').map((token) => token.trim()).filter(Boolean).map((token) => {
      const parts = token.split('*');
      const uyf = parts[0] || '';
      const seri = parts[1] || '';
      const name = parts.slice(2).join('*');
      return { value: `${uyf}*${seri}*${name}`, label: `${uyf}-${seri}-${name}` };
    });
  }

  const names = splitCsv(baslik?.makine);
  const uyfs = String(baslik?.uyf_no || '').split(',').map((part) => part.trim());
  const seris = String(baslik?.seri_no || '').split(',').map((part) => part.trim());
  const count = Math.max(names.length, uyfs.filter(Boolean).length, seris.filter(Boolean).length);
  return Array.from({ length: count }, (_, index) => {
    const name = names[index] || '';
    const uyf = uyfs[index] || '';
    const seri = seris[index] || '';
    return { value: `${uyf}*${seri}*${name}`, label: `${uyf}-${seri}-${name}` };
  }).filter((option) => option.value.replace(/\*/g, '').trim());
};

const conflictIsEmriNo = (error) => {
  const detail = error?.response?.data?.detail;
  if (detail && typeof detail === 'object' && detail.is_emri_no) return detail.is_emri_no;
  return '';
};

const errorDetailText = (error) => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail === 'object') return detail.message || '';
  return error?.message || '';
};

const buildYetkiliOptions = (yetkililerData) => {
  if (!yetkililerData) return [];

  const fields = [
    { name: 'yetkili', eposta: 'eposta', telefon: 'telefon' },
    { name: 'yetkili_iki', eposta: 'eposta_iki', telefon: 'telefon_iki' },
    { name: 'yetkili_uc', eposta: 'eposta_uc', telefon: 'telefon_uc' },
    { name: 'yetkili_dort', eposta: 'eposta_dort', telefon: 'telefon_dort' },
    { name: 'yetkili_bes', eposta: 'eposta_bes', telefon: 'telefon_bes' },
    { name: 'yetkili_alti', eposta: 'eposta_alti', telefon: 'telefon_alti' },
    { name: 'yetkili_yedi', eposta: 'eposta_yedi', telefon: 'telefon_yedi' },
    { name: 'yetkili_sekiz', eposta: 'eposta_sekiz', telefon: 'telefon_sekiz' },
    { name: 'yetkili_dokuz', eposta: 'eposta_dokuz', telefon: 'telefon_dokuz' },
  ];

  return fields
    .filter((field) => yetkililerData[field.name])
    .map((field) => ({
      value: `${yetkililerData[field.name]}*${yetkililerData[field.eposta] || ''}*${yetkililerData[field.telefon] || ''}`,
      label: yetkililerData[field.name],
      yetkili: yetkililerData[field.name],
      eposta: yetkililerData[field.eposta] || '',
      telefon: yetkililerData[field.telefon] || '',
    }));
};

const ToggleTile = ({ active, icon, label, onClick }) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    sx={{
      width: 84,
      height: 68,
      border: 0,
      borderRadius: '18px',
      bgcolor: active ? '#0d86ff' : '#dedede',
      color: active ? '#fff' : '#333',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 0.5,
      cursor: 'pointer',
      font: 'inherit',
      transition: 'background-color 120ms ease, color 120ms ease',
      '& svg': {
        fontSize: 21,
      },
      '&:hover': {
        bgcolor: active ? '#0d86ff' : '#d2d2d2',
      },
    }}
  >
    {icon}
    <Box component="span" sx={{ fontSize: 13, fontWeight: 600, lineHeight: 1 }}>
      {label}
    </Box>
  </Box>
);

const selectStyles = {
  control: (base) => ({
    ...base,
    minHeight: 40,
    height: 'auto',
    borderColor: '#c6d2df',
    borderRadius: 6,
    fontSize: 14,
    boxShadow: 'none',
  }),
  valueContainer: (base) => ({
    ...base,
    minHeight: 38,
    height: 'auto',
    flexWrap: 'wrap',
    padding: '2px 10px',
  }),
  multiValue: (base) => ({
    ...base,
    maxWidth: '100%',
  }),
  indicatorsContainer: (base) => ({
    ...base,
    height: 'auto',
    alignSelf: 'stretch',
  }),
  placeholder: (base) => ({
    ...base,
    color: '#6f7782',
  }),
  menu: (base) => ({
    ...base,
    zIndex: 20,
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 1600,
  }),
};

const fieldLabelSx = {
  display: 'block',
  mb: 0.4,
  color: '#64748b',
  fontSize: 14,
  fontWeight: 500,
};

const textFieldSx = {
  bgcolor: '#fff',
  borderRadius: '6px',
  '& .MuiInputBase-root': {
    height: 40,
    minHeight: 40,
    fontSize: 14,
  },
  '& .MuiInputBase-input': {
    py: 0,
    px: 1.5,
  },
};

const IsEmriTalepForm = ({ createMode = false }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const lockedTenantId = user?.tenant_id != null && user.tenant_id !== "" ? Number(user.tenant_id) : null;
  const params = useParams();
  const [searchParams] = useSearchParams();
  const kaynakTeklifNo = (searchParams.get('teklif_no') || '').trim();
  const isEditMode = !createMode;
  const showGunPlani = isEditMode || Boolean(kaynakTeklifNo);
  const isEmriNoParam = params.is_emri_no;
  const redirectPath = params.redirect || 'taslak-is-emirleri';

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [musteriler, setMusteriler] = useState([]);
  const [lokasyonlar, setLokasyonlar] = useState([]);
  const [makineler, setMakineler] = useState([]);
  const [yetkililer, setYetkililer] = useState([]);
  const [personeller, setPersoneller] = useState([]);
  const [selectedMusteri, setSelectedMusteri] = useState(null);
  const [selectedLokasyon, setSelectedLokasyon] = useState(null);
  const [selectedMakineler, setSelectedMakineler] = useState([]);
  const [selectedYetkili, setSelectedYetkili] = useState(null);
  const [selectedPersoneller, setSelectedPersoneller] = useState([]);
  const [dosyalar, setDosyalar] = useState([]);
  const [modalMakineler, setModalMakineler] = useState([]);
  const [openLokasyonModal, setOpenLokasyonModal] = useState(false);
  const [newLokasyon, setNewLokasyon] = useState({
    musteri_id: '',
    ulke: '',
    sehir: '',
    lokasyon_adi: '',
    telefon: '',
    yetkili: '',
    adres: '',
    makineler: [],
  });

  const [form, setForm] = useState({
    is_emri_no: generateIsEmriNo(),
    baslangic_tarihi: getDefaultStart(),
    bitis_tarihi: getDefaultEnd(),
    musteri: '',
    lokasyonlar: '',
    bolge: 'Türkiye',
    yetkili: '',
    yetkili_eposta: '',
    yetkili_cep_tel: '',
    temsilci: localStorage.getItem('ssh_user_name') || '',
    talep: 'taslak',
    garanti: false,
    kurulum: false,
    servis: false,
    iletilen_problemler: '',
    muhendis: '',
    elektrik_teknikeri: '',
    mekanik_teknisyen: '',
    klasor_konumu: '',
    teslim_sekli: '',
    nakliye_firmasi: '',
    odeme_sekli: '',
    doviz: 'EURO',
    durum: 'Taslak',
  });

  const [gunSatirlari, setGunSatirlari] = useState([]);

  const musteriOptions = useMemo(() => musteriler.map((musteri) => ({
    value: `${musteri.musteri_id}*${musteri.firma}`,
    label: musteri.firma,
    id: musteri.musteri_id,
  })), [musteriler]);

  const lokasyonOptions = useMemo(() => lokasyonlar.map((lokasyon) => ({
    value: `${lokasyon.lokasyon_id}*${lokasyon.lokasyon_adi}`,
    label: lokasyon.lokasyon_adi,
    id: lokasyon.lokasyon_id,
  })), [lokasyonlar]);

  const makineOptions = useMemo(() => makineler.map((makine) => ({
    value: `${makine.siparis_no}*${makine.seri_no}*${makine.makine}`,
    label: `${makine.siparis_no}-${makine.seri_no}-${makine.makine}`,
  })), [makineler]);

  const personelOptions = useMemo(() => personeller
    .filter((personel) => personel.root !== true && personel.root !== 'true' && personel.root !== 1 && personel.root !== '1')
    .map((personel) => {
      const name = getPersonelName(personel);
      return {
        value: getPersonelValue(personel),
        label: name,
        name,
      };
    })
    .filter((option) => option.name)
    .sort((a, b) => a.name.localeCompare(b.name, 'tr')), [personeller]);

  const blankForm = () => ({
    is_emri_no: generateIsEmriNo(),
    baslangic_tarihi: getDefaultStart(),
    bitis_tarihi: getDefaultEnd(),
    musteri: '',
    lokasyonlar: '',
    bolge: 'Türkiye',
    yetkili: '',
    yetkili_eposta: '',
    yetkili_cep_tel: '',
    temsilci: localStorage.getItem('ssh_user_name') || '',
    talep: 'taslak',
    garanti: false,
    kurulum: false,
    servis: false,
    iletilen_problemler: '',
    muhendis: '',
    elektrik_teknikeri: '',
    mekanik_teknisyen: '',
    klasor_konumu: '',
    teslim_sekli: '',
    nakliye_firmasi: '',
    odeme_sekli: '',
    doviz: 'EURO',
    durum: 'Taslak',
  });

  useEffect(() => {
    const loadInitialData = async () => {
      setLoading(true);
      try {
        if (createMode && !kaynakTeklifNo) {
          setForm(blankForm());
          setSelectedMusteri(null);
          setSelectedLokasyon(null);
          setSelectedMakineler([]);
          setSelectedYetkili(null);
          setSelectedPersoneller([]);
          setDosyalar([]);
          setGunSatirlari([]);
          setLokasyonlar([]);
          setMakineler([]);
          setYetkililer([]);
        }
        const [musteriRows] = await Promise.all([fetchMusteriler(), fetchPersoneller()]);
        if (isEditMode && isEmriNoParam) {
          await fetchIsEmri(isEmriNoParam);
        } else if (createMode && kaynakTeklifNo && lockedTenantId == null) {
          await prefillFromTeklif(kaynakTeklifNo, musteriRows);
        }
        if (lockedTenantId != null && createMode) {
          const row = musteriRows.find((item) => Number(item.musteri_id) === lockedTenantId);
          if (row) {
            const option = {
              value: `${row.musteri_id}*${row.firma}`,
              label: row.firma,
              id: row.musteri_id,
            };
            setSelectedMusteri(option);
            setForm((prev) => ({ ...prev, musteri: option.value, lokasyonlar: '' }));
            await fetchLokasyonlar(row.musteri_id);
          }
        }
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, [isEditMode, isEmriNoParam, createMode, kaynakTeklifNo, lockedTenantId]);

  const fetchMusteriler = async () => {
    try {
      const { data } = await api.get('/lookups/musteriler', { params: { limit: 10000 } });
      const rows = Array.isArray(data) ? data : data?.data || [];
      setMusteriler(rows);
      return rows;
    } catch (error) {
      console.error('Müşteriler getirilemedi:', error);
      toast.error('Müşteriler getirilemedi');
      return [];
    }
  };

  const fetchPersoneller = async () => {
    try {
      const { data } = await api.get('/lookups/users', { params: { limit: 2000, scope: 'liste' } });
      const rows = Array.isArray(data) ? data : data?.data || [];
      setPersoneller(rows);
    } catch (error) {
      console.error('Personeller getirilemedi:', error);
      toast.error('Personeller getirilemedi');
    }
  };

  const fetchLokasyonlar = async (musteriId) => {
    if (!musteriId) return [];

    try {
      const { data } = await api.get('/lookups/lokasyonlar', {
        params: { musteri_id: musteriId },
      });
      const rows = Array.isArray(data) ? data : data?.data || [];
      setLokasyonlar(rows);
      return rows;
    } catch (error) {
      console.error('Lokasyonlar getirilemedi:', error);
      toast.error('Lokasyonlar getirilemedi');
      return [];
    }
  };

  const fetchMakineler = async (lokasyonId) => {
    if (!lokasyonId) return [];

    try {
      const { data } = await api.get('/lookups/makineler', {
        params: { lokasyon_id: lokasyonId },
      });
      const rows = Array.isArray(data) ? data : data?.data || [];
      setMakineler(rows);
      return rows;
    } catch (error) {
      console.error('Makineler getirilemedi:', error);
      toast.error('Makineler getirilemedi');
      return [];
    }
  };

  const fetchYetkililer = async (lokasyonId) => {
    if (!lokasyonId) return [];

    try {
      const { data } = await api.get(`/lookups/lokasyonlar/${lokasyonId}/yetkili`);
      const options = buildYetkiliOptions(data);
      setYetkililer(options);
      return options;
    } catch (error) {
      console.error('Yetkililer getirilemedi:', error);
      return [];
    }
  };

  const prefillFromTeklif = async (teklifNo, musteriRows) => {
    try {
      const existing = await api.get(`/is-emirleri/teklif/${encodeURIComponent(teklifNo)}`);
      const mevcutNo = existing.data?.is_emri_no;
      if (mevcutNo) {
        toast.info('Bu teklif zaten iş emrine dönüştürülmüş');
        navigate(`/is-emri-duzenle/${encodeURIComponent(mevcutNo)}/taslak-is-emirleri`);
        return;
      }
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error('Teklif iş emri kontrolü başarısız:', error);
      }
    }

    let baslik;
    try {
      const response = await api.get(`/teklifler/${encodeURIComponent(teklifNo)}`);
      baslik = response.data;
    } catch (error) {
      console.error('Teklif getirilemedi:', error);
      toast.error('Teklif bilgileri getirilemedi');
      return;
    }
    const musteriAdRaw = String(baslik.musteri || '');
    let musteriId = baslik.musteri_id;
    let musteriAd = musteriAdRaw;
    if (musteriAdRaw.includes('*')) {
      const [idPart, ...rest] = musteriAdRaw.split('*');
      if (!musteriId) musteriId = idPart;
      musteriAd = rest.join('*') || musteriAdRaw;
    }
    const musteriRow = (musteriRows || []).find((row) => String(row.musteri_id) === String(musteriId))
      || (musteriRows || []).find((row) => row.firma === musteriAd);
    if (musteriRow) {
      musteriId = musteriRow.musteri_id;
      musteriAd = musteriRow.firma;
    }
    const musteriValue = musteriId ? `${musteriId}*${musteriAd}` : '';
    setSelectedMusteri(musteriValue ? { value: musteriValue, label: musteriAd, id: musteriId } : null);

    let lokasyonId = baslik.lokasyon_id;
    let lokasyonAd = String(baslik.lokasyon || '');
    if (lokasyonAd.includes('*')) {
      const [idPart, ...rest] = lokasyonAd.split('*');
      if (!lokasyonId) lokasyonId = idPart;
      lokasyonAd = rest.join('*');
    }
    const lokasyonList = musteriId ? await fetchLokasyonlar(musteriId) : [];
    const lokasyonRow = lokasyonList.find((row) => String(row.lokasyon_id) === String(lokasyonId))
      || lokasyonList.find((row) => row.lokasyon_adi === lokasyonAd);
    let lokasyonValue = '';
    if (lokasyonRow) {
      lokasyonId = lokasyonRow.lokasyon_id;
      lokasyonAd = lokasyonRow.lokasyon_adi;
      lokasyonValue = `${lokasyonId}*${lokasyonAd}`;
      setSelectedLokasyon({ value: lokasyonValue, label: lokasyonAd, id: lokasyonId });
    }

    let machineChoices = teklifMakineOptions(baslik);
    let fallbackYetkili = null;
    if (lokasyonId) {
      const [makineRows, yetkiliOptions] = await Promise.all([
        fetchMakineler(lokasyonId),
        fetchYetkililer(lokasyonId),
      ]);
      const listed = (makineRows || []).map((makine) => ({
        value: `${makine.siparis_no}*${makine.seri_no}*${makine.makine}`,
        label: `${makine.siparis_no}-${makine.seri_no}-${makine.makine}`,
      }));
      machineChoices = machineChoices.map((option) => listed.find((item) => item.value === option.value) || option);
      const matchedYetkili = yetkiliOptions.find((option) => option.yetkili === baslik.yetkili);
      if (matchedYetkili) setSelectedYetkili(matchedYetkili);
      else if (!baslik.yetkili && yetkiliOptions[0]) {
        fallbackYetkili = yetkiliOptions[0];
        setSelectedYetkili(yetkiliOptions[0]);
      }
    }
    setSelectedMakineler(machineChoices);

    setForm((prev) => ({
      ...prev,
      talep: 'taslak',
      musteri: musteriValue,
      lokasyonlar: lokasyonValue,
      temsilci: baslik.temsilci || prev.temsilci,
      yetkili: baslik.yetkili || fallbackYetkili?.yetkili || '',
      yetkili_eposta: baslik.yetkili ? (baslik.yetkili_eposta || '') : (fallbackYetkili?.eposta || ''),
      yetkili_cep_tel: baslik.yetkili ? (baslik.yetkili_cep_tel || '') : (fallbackYetkili?.telefon || ''),
      doviz: mapTeklifDoviz(baslik.doviz),
      teslim_sekli: baslik.teslim_sekli || '',
      nakliye_firmasi: baslik.nakliye_firmasi || '',
      odeme_sekli: baslik.odeme_sekli || '',
    }));
  };

  const fetchIsEmri = async (isEmriNo) => {
    try {
      const { data } = await api.get(`/is-emirleri/${encodeURIComponent(isEmriNo)}`);
      if (!data) {
        toast.error('İş emri bulunamadı');
        return;
      }

      const musteriValue = `${data.musteri_id}*${data.musteri}`;
      const lokasyonValue = `${data.lokasyon_id}*${data.lokasyon}`;
      const selectedMakineNames = parseJsonArray(data.makineler);
      const selectedUyfler = parseJsonArray(data.uyfler);
      const selectedSeriler = parseJsonArray(data.seri_nolar);
      const makineValues = selectedMakineNames.map((makine, index) => {
        const raw = String(makine || '');
        const encoded = raw.includes('*')
          ? raw
          : `${selectedUyfler[index] || ''}*${selectedSeriler[index] || ''}*${raw}`;
        const parts = encoded.split('*');
        let cursor = 0;
        let uyf = selectedUyfler[index] || '';
        let seri = selectedSeriler[index] || '';
        while (cursor + 2 < parts.length && parts[cursor] && parts[cursor + 1]
          && !/\s/.test(parts[cursor]) && !/\s/.test(parts[cursor + 1])) {
          uyf = parts[cursor];
          seri = parts[cursor + 1];
          cursor += 2;
          if (parts[cursor] === uyf && parts[cursor + 1] === seri) continue;
          break;
        }
        const name = parts.slice(cursor).join('*') || raw;
        return {
          value: `${uyf}*${seri}*${name}`,
          label: `${uyf}-${seri}-${name}`,
        };
      });
      const teknikPersoneller = parseJsonArray(data.teknik_personeller);
      const personelValues = teknikPersoneller.map((personel) => ({
        value: personel,
        label: personel,
        name: personel,
      }));

      setForm((prev) => ({
        ...prev,
        is_emri_no: data.is_emri_no || isEmriNo,
        baslangic_tarihi: splitDateTime(data.is_emri_baslangic_tarihi, data.is_emri_baslangic_saati),
        bitis_tarihi: splitDateTime(data.is_emri_bitis_tarihi, data.is_emri_bitis_saati),
        musteri: musteriValue,
        lokasyonlar: lokasyonValue,
        bolge: data.bolge || 'Türkiye',
        yetkili: data.yetkili || '',
        yetkili_eposta: data.yetkili_eposta || data.yetkili_mail || '',
        yetkili_cep_tel: data.yetkili_cep_tel || data.yetkili_tel || '',
        temsilci: data.temsilci || localStorage.getItem('ssh_user_name') || '',
        talep: data.izin === true || data.izin === '1' || data.izin === 1
          ? 'izin'
          : (data.talep === true || data.talep === '1' || data.talep === 1
            ? 'talep'
            : (data.durum === 'Taslak' ? 'taslak' : '')),
        garanti: data.garanti === true || data.garanti === '1' || data.garanti === 1,
        kurulum: data.kurulum === true || data.kurulum === '1' || data.kurulum === 1,
        servis: data.servis === true || data.servis === '1' || data.servis === 1,
        iletilen_problemler: data.iletilen_problemler || '',
        durum: data.durum || 'Taslak',
        klasor_konumu: data.klasor_konumu || '',
      }));

      setSelectedMusteri({ value: musteriValue, label: data.musteri, id: data.musteri_id });
      setSelectedLokasyon({ value: lokasyonValue, label: data.lokasyon, id: data.lokasyon_id });
      setSelectedMakineler(makineValues);
      setSelectedPersoneller(personelValues);

      try {
        const gunRes = await api.get(`/is-emirleri/${encodeURIComponent(isEmriNo)}/gunler`);
        const guns = Array.isArray(gunRes.data) ? gunRes.data : [];
        setGunSatirlari(guns.map((gun) => ({
          id: gun.id,
          is_emri_tarihi: gun.is_emri_tarihi ? String(gun.is_emri_tarihi).slice(0, 10) : '',
          personeller: parseJsonArray(gun.personeller),
        })));
        const gunTarihleri = guns
          .map((gun) => (gun.is_emri_tarihi ? String(gun.is_emri_tarihi).slice(0, 10) : ''))
          .filter(Boolean)
          .sort();
        setForm((prev) => ({
          ...prev,
          baslangic_tarihi: splitDateTime(data.is_emri_baslangic_tarihi, data.is_emri_baslangic_saati)
            || (gunTarihleri[0] ? `${gunTarihleri[0]}T08:00` : prev.baslangic_tarihi),
          bitis_tarihi: splitDateTime(data.is_emri_bitis_tarihi, data.is_emri_bitis_saati)
            || (gunTarihleri.length ? `${gunTarihleri[gunTarihleri.length - 1]}T18:00` : prev.bitis_tarihi),
        }));
      } catch (gunError) {
        console.error('İş emri günleri getirilemedi:', gunError);
      }

      const lokasyonList = await fetchLokasyonlar(data.musteri_id);
      if (lokasyonList.length > 0) {
        await Promise.all([
          fetchMakineler(data.lokasyon_id),
          fetchYetkililer(data.lokasyon_id).then((options) => {
            const found = options.find((option) => option.yetkili === data.yetkili);
            if (found) setSelectedYetkili(found);
          }),
        ]);
      }
    } catch (error) {
      console.error('İş emri getirilemedi:', error);
      toast.error('İş emri bilgileri getirilemedi');
    }
  };

  const handleFormChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const toggleTalep = (value) => {
    setForm((prev) => {
      const next = prev.talep === value ? '' : value;
      const durum = next === '' || next === 'izin' ? 'Final' : 'Taslak';
      return { ...prev, talep: next, durum };
    });
  };

  const handleMusteriChange = async (option) => {
    setSelectedMusteri(option);
    setSelectedLokasyon(null);
    setSelectedMakineler([]);
    setSelectedYetkili(null);
    setLokasyonlar([]);
    setMakineler([]);
    setYetkililer([]);

    handleFormChange('musteri', option?.value || '');
    handleFormChange('lokasyonlar', '');

    if (option?.id) {
      await fetchLokasyonlar(option.id);
    }
  };

  const handleLokasyonChange = async (option) => {
    setSelectedLokasyon(option);
    setSelectedMakineler([]);
    setSelectedYetkili(null);
    setMakineler([]);
    setYetkililer([]);

    setForm((prev) => ({
      ...prev,
      lokasyonlar: option?.value || '',
      yetkili: '',
      yetkili_eposta: '',
      yetkili_cep_tel: '',
    }));

    if (option?.id) {
      const [yetkiliOptions] = await Promise.all([
        fetchYetkililer(option.id),
        fetchMakineler(option.id),
      ]);

      if (yetkiliOptions.length > 0) {
        handleYetkiliChange(yetkiliOptions[0]);
      }
    }
  };

  const handleYetkiliChange = (option) => {
    setSelectedYetkili(option);
    setForm((prev) => ({
      ...prev,
      yetkili: option?.yetkili || '',
      yetkili_eposta: option?.eposta || '',
      yetkili_cep_tel: option?.telefon || '',
    }));
  };

  const nextGunTarihi = (iso) => {
    if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return '';
    const [year, month, day] = iso.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    date.setDate(date.getDate() + 1);
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  };

  const handleCopyGunSatiri = (index) => {
    setGunSatirlari((prev) => {
      const row = prev[index];
      if (!row) return prev;
      const copy = {
        id: 0,
        is_emri_tarihi: nextGunTarihi(row.is_emri_tarihi),
        personeller: [...(row.personeller || [])],
      };
      const next = [...prev];
      next.splice(index + 1, 0, copy);
      return next;
    });
  };

  const handleAddGunSatiri = () => {
    setGunSatirlari((prev) => {
      const lastDate = [...prev].reverse().find((row) => row.is_emri_tarihi)?.is_emri_tarihi || '';
      return [
        ...prev,
        {
          id: 0,
          is_emri_tarihi: nextGunTarihi(lastDate),
          personeller: [],
        },
      ];
    });
  };

  const handleDeleteGunSatiri = async (index) => {
    const row = gunSatirlari[index];

    if (row.id) {
      try {
        await api.delete(`/is-emirleri/${encodeURIComponent(form.is_emri_no)}/gunler/${row.id}`);
      } catch (error) {
        console.error('Gün satırı silinemedi:', error);
        toast.error('Gün satırı silinemedi');
        return;
      }
    }

    setGunSatirlari((prev) => prev.filter((_, rowIndex) => rowIndex !== index));
  };

  const handleLokasyonEkle = async () => {
    try {
      const makineUyfleri = (newLokasyon.makineler || [])
        .map((makine) => makine.siparis_no || String(makine.value || '').split('*')[0])
        .filter(Boolean);

      await api.post('/lookups/lokasyonlar', {
        ulke: newLokasyon.ulke || null,
        sehir: newLokasyon.sehir || null,
        lokasyon_adi: newLokasyon.lokasyon_adi,
        telefon: newLokasyon.telefon || null,
        yetkili: newLokasyon.yetkili || null,
        adres: newLokasyon.adres || null,
        musteri_id: Number(newLokasyon.musteri_id) || null,
      });
      toast.success('Lokasyon başarıyla eklendi');
      setOpenLokasyonModal(false);
      setNewLokasyon({
        musteri_id: '',
        ulke: '',
        sehir: '',
        lokasyon_adi: '',
        telefon: '',
        yetkili: '',
        adres: '',
        makineler: [],
      });
      setModalMakineler([]);
      fetchMusteriler();
    } catch (error) {
      console.error('Lokasyon eklenemedi:', error);
      toast.error('Lokasyon eklenirken hata oluştu');
    }
  };

  const fetchModalMakineler = async (musteriId) => {
    if (!musteriId) {
      setModalMakineler([]);
      return;
    }

    try {
      const { data } = await api.get(`/lookups/musteriler/${musteriId}/makineler`);
      setModalMakineler(Array.isArray(data) ? data : data?.data || []);
    } catch (error) {
      console.error('Müşteri makineleri getirilemedi:', error);
      toast.error('Müşteri makineleri getirilemedi');
      setModalMakineler([]);
    }
  };

  const handleGunChange = (index, field, value) => {
    setGunSatirlari((prev) => prev.map((row, rowIndex) => (
      rowIndex === index ? { ...row, [field]: value } : row
    )));
  };

  const validateForm = () => {
    if (!form.is_emri_no) return 'İş emri no boş olamaz';
    if (!form.baslangic_tarihi || !form.bitis_tarihi) return 'Başlangıç ve bitiş tarihi zorunlu';
    if (!selectedMusteri) return 'Müşteri seçiniz';
    if (!selectedLokasyon) return 'Lokasyon seçiniz';
    if (selectedPersoneller.length === 0 && form.talep !== 'talep') return 'En az bir teknik personel seçiniz';
    if (!form.yetkili) return 'Yetkili bilgisi zorunlu';
    if (gunSatirlari.some((row) => !row.is_emri_tarihi)) return 'Gün planındaki her satır için tarih seçiniz';
    return '';
  };

  const buildSubmitData = () => {
    const formData = new FormData();
    formData.append('is_emri_no', form.is_emri_no);
    formData.append('baslangic_tarihi', dateTimeLocalToBackend(form.baslangic_tarihi));
    formData.append('bitis_tarihi', dateTimeLocalToBackend(form.bitis_tarihi));
    formData.append('musteri', selectedMusteri?.value || '');
    formData.append('lokasyonlar', selectedLokasyon?.value || '');
    formData.append('bolge', form.bolge || 'Türkiye');
    formData.append('yetkili', form.yetkili || '');
    formData.append('yetkili_eposta', form.yetkili_eposta || '');
    formData.append('yetkili_cep_tel', form.yetkili_cep_tel || '');
    formData.append('temsilci', form.temsilci || localStorage.getItem('ssh_user_name') || '');
    formData.append('talep', form.talep || 'final');
    formData.append('garanti', form.garanti ? '1' : '0');
    formData.append('kurulum', form.kurulum ? '1' : '0');
    formData.append('servis', form.servis ? '1' : '0');
    formData.append('iletilen_problemler', form.iletilen_problemler || '');
    formData.append('muhendis', form.muhendis || '');
    formData.append('elektrik_teknikeri', form.elektrik_teknikeri || '');
    formData.append('mekanik_teknisyen', form.mekanik_teknisyen || '');
    formData.append('talep_departmanlar', '');
    formData.append('klasor_konumu', form.klasor_konumu || '');
    formData.append('teslim_sekli', form.teslim_sekli || '');
    formData.append('nakliye_firmasi', form.nakliye_firmasi || '');
    formData.append('odeme_sekli', form.odeme_sekli || '');
    formData.append('doviz', form.doviz || 'EURO');
    if (kaynakTeklifNo) formData.append('teklif_no', kaynakTeklifNo);
    formData.append('durum', form.durum || 'Taslak');
    formData.append('redirect_url', `/${redirectPath}`);

    selectedMakineler.forEach((makine) => formData.append('makineler[]', makine.value));
    selectedPersoneller.forEach((personel) => formData.append('teknik_personel[]', personel.value));
    // FastAPI list[UploadFile] aynı alan adıyla çoklu part bekler (`dosya[]` değil)
    dosyalar.forEach((file) => formData.append('dosya', file));

    if (isEditMode) {
      gunSatirlari.forEach((row, index) => {
        formData.append('is_emri_tarihi[]', row.is_emri_tarihi || '');
        formData.append('satir_id[]', row.id || 0);
        (row.personeller || []).forEach((personel) => {
          formData.append(`personeller[${index}][]`, personel);
        });
      });
    }

    return formData;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const errorMessage = validateForm();
    if (errorMessage) {
      toast.error(errorMessage);
      return;
    }

    setSubmitting(true);
    try {
      if (isEditMode) {
        const musteriParts = String(selectedMusteri?.value || '').split('*');
        const lokParts = String(selectedLokasyon?.value || '').split('*');
        const no = encodeURIComponent(form.is_emri_no);
        await api.patch(`/is-emirleri/${no}`, {
          musteri: musteriParts.slice(1).join('*') || selectedMusteri?.label,
          musteri_id: parseInt(musteriParts[0], 10) || undefined,
          lokasyon: lokParts.slice(1).join('*') || selectedLokasyon?.label,
          lokasyon_id: parseInt(lokParts[0], 10) || undefined,
          makineler: JSON.stringify(selectedMakineler.map((makine) => {
            const parts = String(makine.value || '').split('*');
            return parts.length >= 3 ? parts.slice(2).join('*') : (makine.label || '');
          })),
          uyfler: JSON.stringify(selectedMakineler.map((makine) => String(makine.value || '').split('*')[0] || '')),
          seri_nolar: JSON.stringify(selectedMakineler.map((makine) => String(makine.value || '').split('*')[1] || '')),
          teknik_personeller: selectedPersoneller.map((p) => p.label || p.name || p.value).join(','),
          yetkili: form.yetkili || undefined,
          yetkili_eposta: form.yetkili_eposta || undefined,
          yetkili_cep_tel: form.yetkili_cep_tel || undefined,
          temsilci: form.temsilci || localStorage.getItem('ssh_user_name') || undefined,
          iletilen_problemler: form.iletilen_problemler || undefined,
          doviz: form.doviz || undefined,
          klasor_konumu: form.klasor_konumu || undefined,
          bolge: form.bolge || undefined,
        });
        // Özel gün satırları
        if (gunSatirlari?.length) {
          await api.put(`/is-emirleri/${no}/gunler`, {
            gunler: gunSatirlari.map((row) => ({
              id: row.id || undefined,
              is_emri_tarihi: row.is_emri_tarihi,
              personeller: row.personeller || [],
            })),
          });
        }
        // Yeni seçilen dosyalar
        for (const file of dosyalar || []) {
          const fd = new FormData();
          fd.append('dosya', file);
          fd.append('slot', 'dosya_bir');
          await api.post(`/is-emirleri/${no}/dosyalar`, fd);
        }
      } else {
        const fd = buildSubmitData();
        // FastAPI Form expects repeated keys without []
        const clean = new FormData();
        for (const [k, v] of fd.entries()) {
          const key = k.replace(/\[\]/g, '');
          clean.append(key, v);
        }
        const { data: created } = await api.post('/is-emirleri/talep', clean);
        const createdNo = created?.is_emri_no || form.is_emri_no;
        if (gunSatirlari.length) {
          try {
            await api.put(`/is-emirleri/${encodeURIComponent(createdNo)}/gunler`, {
              gunler: gunSatirlari.map((row) => ({
                is_emri_tarihi: row.is_emri_tarihi,
                personeller: row.personeller || [],
              })),
            });
          } catch (gunError) {
            console.error('Gün planı yazılamadı:', gunError);
            toast.error('İş emri oluştu, gün planı yazılamadı. Düzenleme ekranından tamamlayın.');
            navigate(`/is-emri-duzenle/${encodeURIComponent(createdNo)}/${form.talep === 'taslak' || form.talep === 'talep' ? 'taslak-is-emirleri' : 'onayli-is-emirleri'}`);
            return;
          }
        }
      }

      toast.success(isEditMode ? 'İş emri güncellendi' : 'İş emri oluşturuldu');
      setTimeout(() => {
        const createdPath = form.talep === 'taslak' || form.talep === 'talep'
          ? '/taslak-is-emirleri'
          : '/onayli-is-emirleri';
        navigate(isEditMode ? `/${redirectPath}` : createdPath);
      }, 800);
    } catch (error) {
      const mevcutNo = conflictIsEmriNo(error);
      if (mevcutNo) {
        toast.info('Bu teklif zaten iş emrine dönüştürülmüş');
        navigate(`/is-emri-duzenle/${encodeURIComponent(mevcutNo)}/taslak-is-emirleri`);
        return;
      }
      console.error('İş emri kaydedilemedi:', error);
      toast.error(`İş emri kaydedilemedi: ${errorDetailText(error) || error.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const renderTalepDepartmanlari = form.talep === 'talep';
  return (
    <Box sx={{ bgcolor: '#eef1f5', minHeight: '100vh' }}>
      <Paper component="form" onSubmit={handleSubmit} sx={{ p: 0, borderRadius: 1, overflow: 'hidden' }}>
        <Box sx={{ height: 116, bgcolor: '#fff', display: 'flex', alignItems: 'center', gap: 12, px: 1 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#2d3158', fontSize: 28 }}>
              İş Emri Ekle
            </Typography>
            {kaynakTeklifNo && (
              <Typography variant="body2" sx={{ color: '#64748b' }}>
                Teklif {kaynakTeklifNo} iş emrine dönüştürülüyor
              </Typography>
            )}
          </Box>
          <Button
            variant="contained"
            onClick={() => setOpenLokasyonModal(true)}
            sx={{
              ml: 'auto',
              bgcolor: '#ff3d00',
              height: 44,
              px: 2,
              fontSize: 14,
              fontWeight: 700,
              boxShadow: '0 3px 8px rgba(255, 61, 0, 0.35)',
              '&:hover': { bgcolor: '#f03800' },
            }}
          >
            Lokasyon Ekle
          </Button>
        </Box>

        <Grid
          container
          spacing={1}
          sx={{
            bgcolor: '#ffb413',
            p: '8px 6px 8px',
            alignItems: 'flex-start',
            m: 0,
            width: '100%',
          }}
        >
          <Grid item xs={12} md={2}>
            <Typography sx={fieldLabelSx}>İş Emri No</Typography>
            <TextField
              value={form.is_emri_no}
              onChange={(e) => handleFormChange('is_emri_no', e.target.value)}
              fullWidth
              size="small"
              required
              sx={textFieldSx}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Typography sx={fieldLabelSx}>İş Emri Tarihi</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.5 }}>
              <TextField
                type="datetime-local"
                value={form.baslangic_tarihi}
                onChange={(e) => handleFormChange('baslangic_tarihi', e.target.value)}
                fullWidth
                size="small"
                required
                InputProps={{ readOnly: isEditMode }}
                sx={textFieldSx}
              />
              <TextField
                type="datetime-local"
                value={form.bitis_tarihi}
                onChange={(e) => handleFormChange('bitis_tarihi', e.target.value)}
                fullWidth
                size="small"
                required
                InputProps={{ readOnly: isEditMode }}
                sx={textFieldSx}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={2}>
            <Typography sx={fieldLabelSx}>Müşteri</Typography>
            <Select
              value={selectedMusteri}
              onChange={handleMusteriChange}
              options={lockedTenantId != null && selectedMusteri ? [selectedMusteri] : musteriOptions}
              isClearable={lockedTenantId == null}
              isDisabled={lockedTenantId != null}
              placeholder="Müşteri seçiniz"
              styles={selectStyles}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Typography sx={fieldLabelSx}>Lokasyon</Typography>
            <Select
              value={selectedLokasyon}
              onChange={handleLokasyonChange}
              options={lokasyonOptions}
              isClearable
              placeholder="Lokasyon seçiniz"
              styles={selectStyles}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Typography sx={fieldLabelSx}>Makine</Typography>
            <Select
              value={selectedMakineler}
              onChange={(options) => setSelectedMakineler(options || [])}
              options={makineOptions}
              isMulti
              placeholder="Makine seçiniz"
              styles={selectStyles}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Typography sx={fieldLabelSx}>EK</Typography>
            <Button
              variant="contained"
              component="label"
              sx={{
                height: 32,
                mt: '6px',
                bgcolor: '#fff',
                color: '#333',
                fontSize: 14,
                textTransform: 'none',
                boxShadow: 'none',
                '&:hover': { bgcolor: '#f4f4f4', boxShadow: 'none' },
              }}
            >
              Dosyaları Seçin
              <input
                type="file"
                hidden
                multiple
                onChange={(e) => setDosyalar(Array.from(e.target.files || []))}
              />
            </Button>
            {dosyalar.length > 0 && (
              <Typography variant="caption" sx={{ display: 'block', color: '#64748b', mt: 0.5 }}>
                {dosyalar.length} dosya
              </Typography>
            )}
          </Grid>

          <Grid item xs={12} md={2}>
            <Typography sx={fieldLabelSx}>İş Emri Bölgesi</Typography>
            <TextField
              select
              value={form.bolge}
              onChange={(e) => handleFormChange('bolge', e.target.value)}
              fullWidth
              size="small"
              sx={textFieldSx}
            >
              <MenuItem value="Türkiye">Türkiye</MenuItem>
              <MenuItem value="Avrupa">Avrupa</MenuItem>
              <MenuItem value="Amerika">Amerika</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12} md={2}>
            <Typography sx={fieldLabelSx}>Teknik Personel</Typography>
            <Select
              value={selectedPersoneller}
              onChange={(options) => setSelectedPersoneller(options || [])}
              options={personelOptions}
              isMulti
              placeholder="Personel seçiniz"
              styles={selectStyles}
            />
          </Grid>

          <Grid item xs={12} md={2}>
            <Typography sx={fieldLabelSx}>Yetkili Seç</Typography>
            <Select
              value={selectedYetkili}
              onChange={handleYetkiliChange}
              options={yetkililer}
              isClearable
              placeholder="Yetkili seçiniz"
              styles={selectStyles}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Typography sx={fieldLabelSx}>Yetkili Adı</Typography>
            <TextField
              placeholder="Yetkili adı giriniz"
              value={form.yetkili}
              onChange={(e) => handleFormChange('yetkili', e.target.value)}
              fullWidth
              size="small"
              required
              sx={textFieldSx}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Typography sx={fieldLabelSx}>Yetkili Eposta Adresi</Typography>
            <TextField
              placeholder="Yetkili eposta adresi giriniz"
              value={form.yetkili_eposta}
              onChange={(e) => handleFormChange('yetkili_eposta', e.target.value)}
              fullWidth
              size="small"
              sx={textFieldSx}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Typography sx={fieldLabelSx}>Yetkili Telefon Numarası</Typography>
            <TextField
              placeholder="Yetkili telefon numarası giriniz"
              value={form.yetkili_cep_tel}
              onChange={(e) => handleFormChange('yetkili_cep_tel', e.target.value)}
              fullWidth
              size="small"
              sx={textFieldSx}
            />
          </Grid>

          <Grid item xs={12}>
            <Box
              sx={{
                width: '100%',
                pt: 0.5,
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 1 }}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 96px)',
                    columnGap: '14px',
                  }}
                >
                <ToggleTile
                  active={form.talep === 'taslak'}
                  icon={<DescriptionIcon />}
                  label="Taslak"
                  onClick={() => toggleTalep('taslak')}
                />
                <ToggleTile
                  active={form.talep === 'talep'}
                  icon={<BusinessCenterIcon />}
                  label="Talep"
                  onClick={() => toggleTalep('talep')}
                />
                <ToggleTile
                  active={form.talep === 'izin'}
                  icon={<ManageAccountsIcon />}
                  label="İzin"
                  onClick={() => toggleTalep('izin')}
                />
                </Box>

                {renderTalepDepartmanlari && (
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(3, 150px)',
                      gap: 1,
                      maxWidth: '100%',
                    }}
                  >
                    <TextField
                      placeholder="Mühendis"
                      type="number"
                      value={form.muhendis}
                      onChange={(e) => handleFormChange('muhendis', e.target.value)}
                      size="small"
                      sx={textFieldSx}
                    />
                    <TextField
                      placeholder="Elektrik Teknikeri"
                      type="number"
                      value={form.elektrik_teknikeri}
                      onChange={(e) => handleFormChange('elektrik_teknikeri', e.target.value)}
                      size="small"
                      sx={textFieldSx}
                    />
                    <TextField
                      placeholder="Mekanik Teknisyen"
                      type="number"
                      value={form.mekanik_teknisyen}
                      onChange={(e) => handleFormChange('mekanik_teknisyen', e.target.value)}
                      size="small"
                      sx={textFieldSx}
                    />
                  </Box>
                )}

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 96px)',
                    columnGap: '14px',
                  }}
                >
                <ToggleTile
                  active={form.kurulum}
                  icon={<ConstructionIcon />}
                  label="Kurulum"
                  onClick={() => handleFormChange('kurulum', !form.kurulum)}
                />
                <ToggleTile
                  active={form.garanti}
                  icon={<ShieldIcon />}
                  label="Garanti"
                  onClick={() => handleFormChange('garanti', !form.garanti)}
                />
                <ToggleTile
                  active={form.servis}
                  icon={<BuildIcon />}
                  label="Servis"
                  onClick={() => handleFormChange('servis', !form.servis)}
                />
                </Box>
              </Box>
              <TextField
                placeholder="İletilen problemleri giriniz"
                value={form.iletilen_problemler}
                onChange={(e) => handleFormChange('iletilen_problemler', e.target.value)}
                multiline
                minRows={8}
                sx={{
                  width: '100%',
                  maxWidth: '100%',
                  bgcolor: '#fff',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '6px',
                    alignItems: 'flex-start',
                    fontSize: 18,
                    color: '#333',
                    '& textarea': {
                      lineHeight: 1.25,
                      px: 1,
                      py: 1,
                    },
                  },
                  '& .MuiOutlinedInput-input::placeholder': {
                    color: '#68717c',
                    opacity: 1,
                  },
                }}
              />
            </Box>
          </Grid>

        </Grid>

        {showGunPlani && (
          <Box sx={{ mt: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Günlük Personel Planı</Typography>
              <Button size="small" startIcon={<AddIcon />} onClick={handleAddGunSatiri}>Satır Ekle</Button>
            </Box>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width={180}>Tarih</TableCell>
                    <TableCell>Personeller</TableCell>
                    <TableCell width={96}></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {gunSatirlari.map((row, index) => (
                    <TableRow key={`${row.id}-${index}`}>
                      <TableCell>
                        <TextField
                          type="date"
                          value={row.is_emri_tarihi}
                          onChange={(e) => handleGunChange(index, 'is_emri_tarihi', e.target.value)}
                          size="small"
                          fullWidth
                        />
                      </TableCell>
                      <TableCell>
                        <Select
                          value={(row.personeller || []).map((personel) => ({ value: personel, label: personel }))}
                          onChange={(options) => handleGunChange(index, 'personeller', (options || []).map((option) => option.value))}
                          options={personelOptions.map((personel) => ({ value: personel.name, label: personel.name }))}
                          isMulti
                          placeholder="Personel seçiniz"
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                          styles={{ menuPortal: (base) => ({ ...base, zIndex: 3000 }) }}
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton title="Kopyala" onClick={() => handleCopyGunSatiri(index)}>
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                        <IconButton color="error" title="Sil" onClick={() => handleDeleteGunSatiri(index)}>
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  {gunSatirlari.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={3}>
                        <Typography variant="body2" color="text.secondary">Gün satırı yok.</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            position: 'sticky',
            bottom: 0,
            zIndex: 2,
            bgcolor: '#fff',
            px: 2,
            py: 1.5,
          }}
        >
          <Button
            type="submit"
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={loading || submitting}
            sx={{ height: 44, fontSize: 14, fontWeight: 700 }}
          >
            {submitting ? 'Kaydediliyor' : 'Kaydet'}
          </Button>
        </Box>
      </Paper>

      <Dialog open={openLokasyonModal} onClose={() => setOpenLokasyonModal(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontSize: '1.1rem', fontWeight: 700, color: '#e59e53' }}>
          Yeni Lokasyon Ekle
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
            <Grid item xs={12} md={6}>
              <Select
                placeholder="Müşteri Seçiniz"
                value={musteriler
                  .map((musteri) => ({
                    value: musteri.musteri_id,
                    label: musteri.firma,
                  }))
                  .find((option) => String(option.value) === String(newLokasyon.musteri_id)) || null}
                options={musteriler.map((musteri) => ({
                  value: musteri.musteri_id,
                  label: musteri.firma,
                }))}
                onChange={(selected) => {
                  const musteriId = selected?.value || '';
                  setNewLokasyon({ ...newLokasyon, musteri_id: musteriId, makineler: [] });
                  fetchModalMakineler(musteriId);
                }}
                styles={selectStyles}
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Select
                placeholder="Makine Seçiniz"
                value={newLokasyon.makineler}
                options={modalMakineler.map((makine) => ({
                  value: `${makine.siparis_no}*${makine.seri_no}*${makine.makine}`,
                  label: `${makine.siparis_no}-${makine.seri_no}-${makine.makine}`,
                  siparis_no: makine.siparis_no,
                }))}
                onChange={(selected) => setNewLokasyon({ ...newLokasyon, makineler: selected || [] })}
                isMulti
                styles={selectStyles}
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth size="small" label="Ülke" value={newLokasyon.ulke} onChange={(e) => setNewLokasyon({ ...newLokasyon, ulke: e.target.value })} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth size="small" label="Şehir" value={newLokasyon.sehir} onChange={(e) => setNewLokasyon({ ...newLokasyon, sehir: e.target.value })} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth size="small" label="Lokasyon Adı" value={newLokasyon.lokasyon_adi} onChange={(e) => setNewLokasyon({ ...newLokasyon, lokasyon_adi: e.target.value })} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth size="small" label="Telefon" value={newLokasyon.telefon} onChange={(e) => setNewLokasyon({ ...newLokasyon, telefon: e.target.value })} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth size="small" label="Yetkili" value={newLokasyon.yetkili} onChange={(e) => setNewLokasyon({ ...newLokasyon, yetkili: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" multiline rows={2} label="Adres" value={newLokasyon.adres} onChange={(e) => setNewLokasyon({ ...newLokasyon, adres: e.target.value })} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => setOpenLokasyonModal(false)}>İptal</Button>
          <Button size="small" onClick={handleLokasyonEkle} variant="contained" sx={{ bgcolor: '#e59e53', '&:hover': { bgcolor: '#d18a43' } }}>
            Kaydet
          </Button>
        </DialogActions>
      </Dialog>

      <ToastContainer position="bottom-right" autoClose={4000} />
    </Box>
  );
};

export default IsEmriTalepForm;
